import React from 'react';
import { Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import Badge from 'src/components/Badge';
import {
  ClusterStatusItem,
  SolutionEdgeWorkloadItem,
  SolutionEdgeWorkloadStatus as Status,
} from '../../types/solutionEdgeWorkload';

type Props = {
  value?: string;
  item?: SolutionEdgeWorkloadItem;
};

function getClusterBadgeColor(status: string): 'success' | 'warning' | 'error' | 'default' | 'info' {
  const s = status.toLowerCase();
  if (s === Status.active) return 'success';
  if (s === Status.failed || s === Status.errApplied) return 'error';
  if (s === Status.notReady) return 'warning';
  if (s === Status.paused) return 'info';
  return 'default';
}

function getDeploymentBadge(
  deploymentPaused: boolean,
  deploymentStatus: string,
  activeCount: number,
  totalCount: number,
  deploymentSummary: string
): { color: 'success' | 'warning' | 'error' | 'default' | 'info'; label: string } {
  if (deploymentPaused) {
    return { color: 'info', label: deploymentStatus || 'Paused' };
  }
  if (totalCount > 0 && activeCount === totalCount) {
    return { color: 'success', label: `${deploymentSummary} Active` };
  }
  if (totalCount > activeCount && activeCount > 0) {
    return { color: 'warning', label: `${deploymentSummary} Error` };
  }
  if (totalCount > 0 && activeCount === 0) {
    return { color: 'error', label: `${deploymentSummary} Failed` };
  }
  return { color: 'default', label: deploymentSummary || '-' };
}

function SolutionGitRepoClusterStatus({ value = '' }: Props) {
  let statusData: any = {};
  try {
    statusData = value ? JSON.parse(value) : {};
  } catch {
    statusData = {};
  }
  const clusterStatuses: ClusterStatusItem[] =
    statusData?.clusterStatuses ?? [];
  const deploymentSummary: string = statusData?.deploymentSummary ?? '';
  const activeDeploymentCount: number = statusData?.activeDeploymentCount ?? 0;
  const totalDeploymentCount: number = statusData?.totalDeploymentCount ?? 0;
  const deploymentPaused: boolean = statusData?.deploymentPaused ?? false;

  // Fallback if no cluster statuses
  if (!clusterStatuses || clusterStatuses.length === 0) {
    const badge = getDeploymentBadge(
      deploymentPaused,
      statusData?.status || '',
      activeDeploymentCount,
      totalDeploymentCount,
      deploymentSummary
    );
    return <Badge color={badge.color} label={badge.label} />;
  }

  const badge = getDeploymentBadge(
    deploymentPaused,
    statusData?.status || '',
    activeDeploymentCount,
    totalDeploymentCount,
    deploymentSummary
  );

  // Show status badge + per-cluster breakdown table
  return (
    <Box>
      <Box sx={{ mb: 1 }}>
        <Badge color={badge.color} label={badge.label} />
      </Box>

      {/* Per-cluster breakdown table */}
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, fontSize: '13px', padding: '6px 12px' }}>
                Cluster
              </TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '13px', padding: '6px 12px' }}>
                Status
              </TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '13px', padding: '6px 12px' }}>
                Message
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {clusterStatuses.map((cs) => {
              const color = getClusterBadgeColor(cs.status);
              return (
                <TableRow key={cs.clusterId}>
                  <TableCell sx={{ fontSize: '13px', color: '#424242', padding: '6px 12px' }}>
                    {cs.clusterName || cs.clusterId}
                  </TableCell>
                  <TableCell sx={{ padding: '6px 12px' }}>
                    <Badge
                      color={color}
                      label={cs.status.charAt(0).toUpperCase() + cs.status.slice(1)}
                    />
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: '12px',
                      color: '#DB1E36',
                      padding: '6px 12px',
                      wordBreak: 'break-word',
                    }}
                  >
                    {cs.deploymentErrorMessage || '-'}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

export default SolutionGitRepoClusterStatus;
